import { defineVariants } from "../../style-engine/define-variants";

// ── Table container ───────────────────────────────────────────────────────────

/**
 * Color variants set per-level elevation tokens as CSS vars so all child CEs
 * (`e-table-cell`, `e-table-header-cell`) can reference them via `--table-color-{n}`.
 * Variant variants compose those level vars into semantic `--table-*` tokens that
 * cascade into the child CE shadow roots.
 */
export const tableVariants = defineVariants({
  base: "grid w-full rounded-lg overflow-hidden",
  variants: {
    color: {
      primary: `
        [--table-color-50:var(--color-primary-elevation-50)]
        [--table-color-100:var(--color-primary-elevation-100)]
        [--table-color-200:var(--color-primary-elevation-200)]
        [--table-color-300:var(--color-primary-elevation-300)]
        [--table-color-400:var(--color-primary-elevation-400)]
        [--table-color-600:var(--color-primary-elevation-600)]
        [--table-color-700:var(--color-primary-elevation-700)]
        [--table-color-800:var(--color-primary-elevation-800)]
        [--table-base-color:var(--color-primary)]
        [--table-on-color:white]
      `,
      secondary: `
        [--table-color-50:var(--color-secondary-elevation-50)]
        [--table-color-100:var(--color-secondary-elevation-100)]
        [--table-color-200:var(--color-secondary-elevation-200)]
        [--table-color-300:var(--color-secondary-elevation-300)]
        [--table-color-400:var(--color-secondary-elevation-400)]
        [--table-color-600:var(--color-secondary-elevation-600)]
        [--table-color-700:var(--color-secondary-elevation-700)]
        [--table-color-800:var(--color-secondary-elevation-800)]
        [--table-base-color:var(--color-secondary)]
        [--table-on-color:white]
      `,
      accent: `
        [--table-color-50:var(--color-accent-elevation-50)]
        [--table-color-100:var(--color-accent-elevation-100)]
        [--table-color-200:var(--color-accent-elevation-200)]
        [--table-color-300:var(--color-accent-elevation-300)]
        [--table-color-400:var(--color-accent-elevation-400)]
        [--table-color-600:var(--color-accent-elevation-600)]
        [--table-color-700:var(--color-accent-elevation-700)]
        [--table-color-800:var(--color-accent-elevation-800)]
        [--table-base-color:var(--color-accent)]
        [--table-on-color:white]
      `,
      error: `
        [--table-color-50:var(--color-error-elevation-50)]
        [--table-color-100:var(--color-error-elevation-100)]
        [--table-color-200:var(--color-error-elevation-200)]
        [--table-color-300:var(--color-error-elevation-300)]
        [--table-color-400:var(--color-error-elevation-400)]
        [--table-color-600:var(--color-error-elevation-600)]
        [--table-color-700:var(--color-error-elevation-700)]
        [--table-color-800:var(--color-error-elevation-800)]
        [--table-base-color:var(--color-error)]
        [--table-on-color:white]
      `,
      warning: `
        [--table-color-50:var(--color-warning-elevation-50)]
        [--table-color-100:var(--color-warning-elevation-100)]
        [--table-color-200:var(--color-warning-elevation-200)]
        [--table-color-300:var(--color-warning-elevation-300)]
        [--table-color-400:var(--color-warning-elevation-400)]
        [--table-color-600:var(--color-warning-elevation-600)]
        [--table-color-700:var(--color-warning-elevation-700)]
        [--table-color-800:var(--color-warning-elevation-800)]
        [--table-base-color:var(--color-warning)]
        [--table-on-color:white]
      `,
      info: `
        [--table-color-50:var(--color-info-elevation-50)]
        [--table-color-100:var(--color-info-elevation-100)]
        [--table-color-200:var(--color-info-elevation-200)]
        [--table-color-300:var(--color-info-elevation-300)]
        [--table-color-400:var(--color-info-elevation-400)]
        [--table-color-600:var(--color-info-elevation-600)]
        [--table-color-700:var(--color-info-elevation-700)]
        [--table-color-800:var(--color-info-elevation-800)]
        [--table-base-color:var(--color-info)]
        [--table-on-color:white]
      `,
      success: `
        [--table-color-50:var(--color-success-elevation-50)]
        [--table-color-100:var(--color-success-elevation-100)]
        [--table-color-200:var(--color-success-elevation-200)]
        [--table-color-300:var(--color-success-elevation-300)]
        [--table-color-400:var(--color-success-elevation-400)]
        [--table-color-600:var(--color-success-elevation-600)]
        [--table-color-700:var(--color-success-elevation-700)]
        [--table-color-800:var(--color-success-elevation-800)]
        [--table-base-color:var(--color-success)]
        [--table-on-color:white]
      `,
      neutral: `
        [--table-color-50:var(--color-elevation-50)]
        [--table-color-100:var(--color-elevation-100)]
        [--table-color-200:var(--color-elevation-200)]
        [--table-color-300:var(--color-elevation-300)]
        [--table-color-400:var(--color-elevation-400)]
        [--table-color-600:var(--color-elevation-600)]
        [--table-color-700:var(--color-elevation-700)]
        [--table-color-800:var(--color-elevation-800)]
        [--table-base-color:var(--color-neutral)]
        [--table-on-color:white]
      `,
    },
    variant: {
      subtle: `
        [--table-header-bg:var(--table-color-50)]
        [--table-header-text:var(--table-color-800)]
        [--table-header-hover-bg:var(--table-color-100)]
        [--table-sorted-bg:var(--table-color-100)]
        [--table-sorted-text:var(--table-color-800)]
        [--table-handle-bar:var(--table-color-300)]
        [--table-border:var(--color-border)]
        [--table-stripe-bg-even:var(--table-color-50)]
        [--table-stripe-bg-odd:transparent]
        [--table-row-rounded:0]
        [--table-header-row-rounded:0]
      `,
      soft: `
        [--table-header-bg:var(--table-color-100)]
        [--table-header-text:var(--table-color-700)]
        [--table-header-hover-bg:var(--table-color-200)]
        [--table-sorted-bg:var(--table-color-200)]
        [--table-sorted-text:var(--table-color-800)]
        [--table-handle-bar:var(--table-color-400)]
        [--table-border:var(--color-border)]
        [--table-stripe-bg-even:var(--table-color-50)]
        [--table-stripe-bg-odd:transparent]
        [--table-row-rounded:0]
        [--table-header-row-rounded:0]
      `,
      outline: `
        [--table-header-bg:transparent]
        [--table-header-text:var(--color-foreground)]
        [--table-header-hover-bg:var(--table-color-50)]
        [--table-sorted-bg:var(--table-color-100)]
        [--table-sorted-text:var(--table-color-800)]
        [--table-handle-bar:var(--table-color-300)]
        [--table-border:var(--color-border)]
        [--table-stripe-bg-even:var(--table-color-50)]
        [--table-stripe-bg-odd:transparent]
        [--table-row-rounded:0]
        [--table-header-row-rounded:0]
      `,
      solid: `
        [--table-header-bg:var(--table-color-600)]
        [--table-header-text:var(--table-on-color,white)]
        [--table-header-hover-bg:var(--table-color-700)]
        [--table-sorted-bg:var(--table-color-700)]
        [--table-sorted-text:var(--table-on-color,white)]
        [--table-handle-bar:rgba(255,255,255,0.4)]
        [--table-border:var(--color-border)]
        [--table-stripe-bg-even:var(--table-color-50)]
        [--table-stripe-bg-odd:transparent]
        [--table-row-rounded:0]
        [--table-header-row-rounded:0]
      `,
      transparent: `
        [--table-header-bg:oklch(from_var(--table-base-color)_l_c_h_/_0.1)]
        [--table-header-text:var(--color-foreground)]
        [--table-header-hover-bg:oklch(from_var(--table-base-color)_l_c_h_/_0.05)]
        [--table-sorted-bg:oklch(from_var(--table-base-color)_l_c_h_/_0.1)]
        [--table-sorted-text:var(--color-foreground)]
        [--table-handle-bar:oklch(from_var(--table-base-color)_l_c_h_/_0.3)]
        [--table-border:transparent]
        [--table-stripe-bg-even:oklch(from_var(--table-base-color)_l_c_h_/_0.05)]
        [--table-stripe-bg-odd:transparent]
        [--table-row-rounded:0.375rem]
        [--table-header-row-rounded:0.375rem]
      `,
    },
    size: {
      xs: `
        [--table-cell-px:0.5rem] [--table-cell-py:0.25rem]
        [--table-header-px:0.5rem] [--table-header-py:0.375rem]
        [--table-cell-font-size:0.6875rem] [--table-header-font-size:0.6875rem]
      `,
      sm: `
        [--table-cell-px:0.75rem] [--table-cell-py:0.375rem]
        [--table-header-px:0.75rem] [--table-header-py:0.5rem]
        [--table-cell-font-size:0.75rem] [--table-header-font-size:0.75rem]
      `,
      md: `
        [--table-cell-px:1rem] [--table-cell-py:0.625rem]
        [--table-header-px:1rem] [--table-header-py:0.625rem]
        [--table-cell-font-size:0.875rem] [--table-header-font-size:0.875rem]
      `,
      lg: `
        [--table-cell-px:1.25rem] [--table-cell-py:0.875rem]
        [--table-header-px:1.25rem] [--table-header-py:0.875rem]
        [--table-cell-font-size:0.875rem] [--table-header-font-size:0.875rem]
      `,
      xl: `
        [--table-cell-px:1.5rem] [--table-cell-py:1.125rem]
        [--table-header-px:1.5rem] [--table-header-py:1.125rem]
        [--table-cell-font-size:1rem] [--table-header-font-size:0.875rem]
      `,
    },
    bordered: {
      true: "border border-(--color-border)",
      false: "",
    },
  },
  defaults: {
    color: "neutral",
    variant: "transparent",
    size: "md",
    bordered: "false",
  },
});

// ── Table head ────────────────────────────────────────────────────────────────

export const tableHeadVariants = defineVariants({
  base: "col-span-full grid [grid-template-columns:subgrid] overflow-hidden [border-bottom-left-radius:var(--table-header-row-rounded,0)] [border-bottom-right-radius:var(--table-header-row-rounded,0)]",
  variants: {
    sticky: {
      true: "sticky top-0 z-10",
      false: "",
    },
  },
  defaults: { sticky: "false" },
});

// ── Table body ────────────────────────────────────────────────────────────────

export const tableBodyVariants = defineVariants({
  base: "contents",
  variants: {
    scroll: {
      vertical: "!grid col-span-full [grid-template-columns:subgrid] overflow-y-auto scroll-smooth",
      horizontal: "!grid col-span-full [grid-template-columns:subgrid] overflow-x-auto scroll-smooth",
      both: "!grid col-span-full [grid-template-columns:subgrid] overflow-auto scroll-smooth",
    },
  },
  defaults: {},
});

// ── Table footer ─────────────────────────────────────────────────────────────

export const tableFooterVariants = defineVariants({
  base: "contents",
  variants: {
    sticky: {
      true: "sticky bottom-0 z-10 bg-(--color-background)",
      false: "",
    },
  },
  defaults: { sticky: "false" },
});

// ── Table row ────────────────────────────────────────────────────────────────

export const tableRowVariants = defineVariants({
  base: "col-span-full grid [grid-template-columns:subgrid] transition-colors",
  wrapper: "contents",
  variants: {
    disabled: {
      true: "opacity-50 cursor-not-allowed pointer-events-none",
      false: "",
    },
    stripe: {
      even: "bg-(--table-stripe-bg-even) [border-radius:var(--table-row-rounded,0)]",
      odd: "bg-(--table-stripe-bg-odd)",
      none: "",
    },
    hoverable: {
      true: "hover:bg-(--table-color-100)! cursor-pointer",
      false: "",
    },
    selected: {
      true: "bg-(--table-color-100)!",
      false: "",
    },
  },
  defaults: {
    disabled: "false",
    stripe: "none",
    hoverable: "false",
    selected: "false",
  },
});

// ── Table cell ───────────────────────────────────────────────────────────────

export const tableCellVariants = defineVariants({
  base: "min-w-0 [padding-block:var(--table-cell-py,0.625rem)] [padding-inline:var(--table-cell-px,1rem)] [font-size:var(--table-cell-font-size,0.875rem)] text-(--color-foreground) border-b border-(--table-border)",
  variants: {
    textOverflow: {
      wrap: "",
      truncate: "truncate overflow-hidden whitespace-nowrap",
      ellipsis: "overflow-hidden text-ellipsis",
      clip: "overflow-hidden",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
    valign: {
      top: "align-top",
      middle: "align-middle",
      bottom: "align-bottom",
    },
  },
  defaults: {
    textOverflow: "wrap",
    align: "left",
    valign: "middle",
  },
});

// ── Table header cell ─────────────────────────────────────────────────────────

export const tableHeaderCellVariants = defineVariants({
  base: "relative flex overflow-hidden bg-(--table-header-bg) text-(--table-header-text) [padding-block:var(--table-header-py,0.625rem)] [padding-inline:var(--table-header-px,1rem)] [font-size:var(--table-header-font-size,0.875rem)] font-heading font-semibold lowercase border-b border-(--table-border)",
  sortIcon: "ml-auto inline-flex shrink-0 items-center gap-1 pl-2",
  handle: "resize-handle absolute right-0 top-0 h-full w-2 cursor-col-resize",
  handleBar: "relative h-6 mx-1 my-2 w-px bg-(--table-handle-bar)",
  variants: {
    sortable: {
      true: "cursor-pointer select-none hover:bg-(--table-header-hover-bg)",
      false: "",
    },
    sorted: {
      true: "bg-(--table-sorted-bg) text-(--table-sorted-text)",
      false: "",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
  },
  defaults: {
    sortable: "false",
    sorted: "false",
    align: "left",
  },
});
